import { Box, Button, Grid2 } from '@mui/material';
import { BookingOrigin, BookingStatus } from '@utriper/core';
import { DateRangePickerWithInput, LookUpContract, LookUpExperience, SelectCheckbox, SelectComponent, TIME_ZONES } from '@utriper/core-ui';
import { EntityService } from 'core/api/services/lookUp';
import { useReport } from 'core/contexts/reports';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ReportFilter = () => {
    const { filters, updateFilter, loading, getHostReports } = useReport();
    const { t: translate, i18n } = useTranslation();

    const statusOptions = useMemo(
        () =>
            BookingStatus.getUiAllowedStatusId().map((id) => ({
                id,
                name: BookingStatus.getTranslatedName(BookingStatus.status, id, i18n.language)?.[0] ?? '',
            })),
        [i18n.language]
    );

    const bookingOriginOptions = useMemo(
        () =>
            BookingOrigin.getValidOrigins().map((id) => ({
                id,
                name: BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18n.language)?.[0] ?? '',
            })),
        [i18n.language]
    );

    const timezoneOptions = TIME_ZONES.map(({ id, name }) => ({ id, name, value: name }));

    const handleDateRangeChange = ({ startDate, endDate }) => {
        if (!startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        const minAllowedDate = DateTime.utc(2026, 1, 1, 0, 0);

        // Validation 1: No dates earlier than 01/01/2026
        if (startDate.toMillis() < minAllowedDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 2: The end date is later than the start date.
        if (endDate && endDate.toMillis() < startDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 3: No range bigger than 365 days
        if (endDate) {
            const daysDifference = Math.ceil(endDate.diff(startDate, 'days')?.days);
            if (daysDifference > 365) {
                toast.error(translate('reports.filters.range_too_large'), { position: 'top-right', theme: 'colored' });
                return false;
            }
        }

        updateFilter('startDate', startDate);
        updateFilter('endDate', endDate);
    };

    const handleFetchReports = async () => {
        if (!filters?.startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        await getHostReports();
    };

    return (
        <Grid2 container spacing={1} width="full" size={12} marginBottom={2} flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
            {/* Dates */}
            <DateRangePickerWithInput
                value={{ startDate: filters?.startDate, endDate: filters?.endDate }}
                onChange={handleDateRangeChange}
                style={{
                    width: '100%',
                    minWidth: '280px',
                }}
                useUTC
            />

            {/* Timezone */}
            <Box
                sx={{
                    width: '100%',
                    '& .MuiSelect-select': { padding: '14px' },
                }}>
                <SelectComponent
                    label={translate('reports.filters.timezone')}
                    value={filters?.timezone}
                    values={timezoneOptions}
                    onChange={({ id }) => updateFilter('timezone', id)}
                    disabled={loading}
                    style={{
                        width: '100%',
                        minWidth: '280px',
                    }}
                />
            </Box>

            {/* Booking Status */}
            <SelectCheckbox
                label={translate('reports.filters.status')}
                options={statusOptions}
                values={filters?.paymentStatus || []}
                onChange={(status) => updateFilter('paymentStatus', status)}
                disabled={loading}
                sx={{
                    width: '100%',
                    minWidth: { xs: '280px', lg: 'auto' },
                }}
            />

            {/* Origins */}
            <SelectCheckbox
                label={translate('reports.filters.origin')}
                options={bookingOriginOptions}
                values={filters?.originIds || []}
                onChange={(originId) => updateFilter('originIds', originId)}
                disabled={loading}
                sx={{
                    width: '100%',
                    minWidth: { xs: '280px', lg: 'auto' },
                }}
            />

            {/* Contract Ids */}
            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpContract
                    label={translate('reports.filters.contract')}
                    value={filters?.contractIds || []}
                    onChange={(val) => {
                        updateFilter('contractIds', Array.isArray(val) ? val.map((v) => v) : [val]);
                    }}
                    entityService={EntityService}
                    disabled={loading}
                    multiSelect
                />
            </Grid2>

            {/* Experience Ids */}
            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpExperience
                    label={translate('reports.filters.experience')}
                    value={filters?.experienceIds || []}
                    onChange={(val) => {
                        updateFilter('experienceIds', Array.isArray(val) ? val.map((v) => v) : [val]);
                    }}
                    entityService={EntityService}
                    disabled={loading}
                    multiSelect
                />
            </Grid2>

            {/* Submit Button */}
            <Button
                variant="contained"
                color="primary"
                onClick={handleFetchReports}
                disabled={loading}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 'fit-content',
                }}>
                {translate('reports.filters.get')}
            </Button>
        </Grid2>
    );
};

export default ReportFilter;
